import Constants from '@/Constants';
import { BotRecommendationRequest } from '@/models/BotRecommendationRequest';
import { RichContent } from '@/models/Chat';
import Item from '@/models/Item';
import { analyticsService, BotEvents } from '@/services/AnalyticsService';
import { contentService } from '@/services/ContentService';
import { ApplicationState } from '@/store';
import { closeChatModal, getBotRecommendationsRequest, selectRecommendationContentRequest } from '@/store/bot/actions';
import { ContentStudentView } from '@/store/content/types';
import { getFavoriteListRequest } from '@/store/favorite/actions';
import { Favorite } from '@/store/favorite/types';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import _ from 'underscore';
import './style.scss';

interface Props {
    data: RichContent;
}

const ChatContentMessage: React.FC<Props> = (props: Props) => {
    const { data } = props;
    const { strings, buildingContentRecommendations, favoriteList } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        buildingContentRecommendations: state.chat.buildingContentRecommendations,
        favoriteList: state.favorite.favoriteList,
    }));

    const dispatch = useDispatch();

    const recommendationCompleted: boolean = data.recomendationResult && data.recomendationResult.botRecommendationId;

    const ratingValidation = (content: Item) => {
        return content.rating ? content.rating.toFixed(1) : '-';
    };

    const doRequest = (requestData: BotRecommendationRequest) => {
        let { classificationIds } = requestData;

        if (typeof requestData.classificationIds === 'object') {
            classificationIds = ((classificationIds.slice(-2) as unknown) as number[]).join(',');
        }

        const request: BotRecommendationRequest = {
            classificationIds,
            contentTypes: requestData.contentTypes,
            categoryIds: requestData.categoryIds,
        } as BotRecommendationRequest;

        dispatch(getBotRecommendationsRequest(request));
    };

    const onOpenContent = (contentStudentView: ContentStudentView): void => {
        dispatch(closeChatModal());
        dispatch(
            selectRecommendationContentRequest({
                recommendationId: data.recomendationResult?.botRecommendationId,
                contentId: contentStudentView.content.id,
            } as BotRecommendationRequest),
        );
        analyticsService.logEvent(BotEvents.ACCESS_ITEM_RECOMMENDATION_CHAT, {
            entityId: contentStudentView.content.id,
            entityName: contentStudentView.content.name,
            entityType: Constants.entityTypes.CONTENT,
        });
    };

    useEffect(() => {
        if (!recommendationCompleted) {
            dispatch(getFavoriteListRequest(false));
        }
    }, []);

    useEffect(() => {
        if (!recommendationCompleted && favoriteList.length > 0) {
            const favList: Favorite[] = favoriteList.filter((favorite: Favorite) => favorite.contentType === Constants.entityTypes.CATEGORY);
            const categoryList: number[] = _.pluck(favList, 'contentId');
            const categoryIds = buildingContentRecommendations.categoryIds || categoryList.join(',');
            doRequest({ ...buildingContentRecommendations, categoryIds });
        }
    }, [favoriteList.length]);

    return (
        <>
            {data.recomendationResult && data.recomendationResult.listContentStudentViewResult.total > 0 && (
                <div className="card-message">
                    {data.recomendationResult.listContentStudentViewResult.contentStudentViewList.map((csv: ContentStudentView) => (
                        <Link
                            className="link-to-content"
                            key={_.uniqueId('contentCard_')}
                            onClick={() => onOpenContent(csv)}
                            to={`/${Constants.CONTENTS_RESOURCE_NAME}/${csv.content.id}`}
                        >
                            <div className="card-item">
                                <div className="card-item__body">
                                    <div className="card-item__label">{contentService.getContentMetadata(csv.content).typeName}</div>
                                    <h3 className="card-item__title" title={csv.content.name}>
                                        {csv.content.name}
                                    </h3>
                                </div>
                                <div className="card-item__thumb">
                                    <img alt="" className="card-item__thumb__img" src={csv.content.mainImage} />
                                </div>
                                <div className="card-item__footer">
                                    <div className="card-item__duration">{`${csv.content.workload} ${strings.MINUTES}`}</div>
                                    <div className="card-item__rating">
                                        <i className="material-icons">grade</i>
                                        <span>{ratingValidation(csv.content)}</span>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </>
    );
};

export default ChatContentMessage;
